import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"

// Image carousel with touch swipe gestures
// Clean implementation using manual addEventListener for precise touch control
export default class extends Controller {
  static targets = ["slide", "indicator", "counter", "previousButton", "nextButton", "imageContainer"]
  static values = {
    currentIndex: { type: Number, default: 0 },
    autoSlide: { type: Boolean, default: false },
    // Pointer/swipe state
    touchStartX: { type: Number, default: 0 },
    touchStartY: { type: Number, default: 0 },
    touchCurrentX: { type: Number, default: 0 },
    isSwiping: { type: Boolean, default: false },
    // Status bar brightness analysis constants
    // sampleHeight: px height to sample from image top (covers ~24-32dp status bar * 2x density)
    statusBarSampleHeight: { type: Number, default: 60 },
    // brightnessThreshold: ITU-R BT.709 midpoint (0-255 range), >= threshold = light background
    brightnessDarkThreshold: { type: Number, default: 128 },
    // Native carousel UI data (iOS only)
    isOwner: { type: Boolean, default: false },
    shareTitle: { type: String, default: "" },
    shareText: { type: String, default: "" },
    shareUrl: { type: String, default: "" },
    editUrl: { type: String, default: "" },
    // Profile image overlay (Ads carousel only)
    profileImageUrl: { type: String, default: "" }
  }

  // Maximum brightness cache entries (product images ~5 per page × 10 pages buffer)
  static BRIGHTNESS_CACHE_MAX_SIZE = 50

  connect() {
    this.autoSlideInterval = null
    this.activePointerId = null
    this.pointerStartTime = 0
    this.pointerLockedAxis = null
    this.lastPointerX = null
    this.pendingAnimationTimeout = null  // Track animation timeout for cancellation
    this.brightnessCache = new Map()  // Cache for image brightness analysis (img.src → brightness)
    this._isIOSNative = null  // Cached iOS native app detection result
    this._isAndroidNative = null  // Cached Android native app detection result
    this._carouselBounceDisabled = false  // Track if native bounce is disabled (iOS double bounce fix)
    this._nativeCarouselMounted = false  // Track if iOS native carousel overlay is active

    // NaN defense: Validate initial value (handles invalid DOM attributes or cache issues)
    if (!Number.isFinite(this.currentIndexValue)) {
      this.currentIndexValue = 0
    }

    this.updateDisplay()

    // iOS: Use native carousel overlay for 60fps swipe experience (including single image)
    if (this.isIOSNative() && this.slideTargets.length >= 1) {
      this.mountNativeCarousel()
    } else {
      // Android/Web: Use JS carousel with pointer events
      this.bindPointerEvents()
    }

    // Start auto-slide if enabled (chat tip slider only)
    if (this.autoSlideValue) {
      this.startAutoSlide()
    }

    // Handle visibility change (e.g., returning from native gallery)
    // Reset state to prevent gesture confusion after app switch
    this.handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        this.clearAllTransforms()
        this.resetPointerState()
      }
    }
    document.addEventListener("visibilitychange", this.handleVisibilityChange)

    // Turbo navigation: Unmount native carousel before page transition
    // This ensures the native overlay is removed before the WebView navigates
    this.handleBeforeVisit = () => {
      if (this._nativeCarouselMounted) {
        this.unmountNativeCarousel()
      }
    }
    document.addEventListener("turbo:before-visit", this.handleBeforeVisit)

    // iOS z-order fix: Hide native carousel when in-page modals open
    // Native overlays render above WebView, hiding turbo-frame modals (e.g., report form)
    if (this.isIOSNative()) {
      this.observeModalFrames()
    }
  }

  disconnect() {
    // iOS: Unmount native carousel overlay
    if (this._nativeCarouselMounted) {
      this.unmountNativeCarousel()
    }

    this.stopAutoSlide()
    this.unbindPointerEvents()

    // Clean up pending animation timeout to prevent orphaned callbacks
    if (this.pendingAnimationTimeout) {
      clearTimeout(this.pendingAnimationTimeout)
      this.pendingAnimationTimeout = null
    }

    // Clean up visibility listener
    if (this.handleVisibilityChange) {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange)
    }

    // Clean up native carousel event listeners
    if (this._unsubscribeIndexChanged) {
      this._unsubscribeIndexChanged()
      this._unsubscribeIndexChanged = null
    }
    if (this._unsubscribeImageLoaded) {
      this._unsubscribeImageLoaded()
      this._unsubscribeImageLoaded = null
    }

    // Clean up Turbo navigation listener
    if (this.handleBeforeVisit) {
      document.removeEventListener("turbo:before-visit", this.handleBeforeVisit)
    }

    // Clean up modal frame observer
    if (this._modalFrameObserver) {
      this._modalFrameObserver.disconnect()
      this._modalFrameObserver = null
    }
  }

  // NaN auto-recovery: Stimulus value changed callback
  // Restores to previous value or 0 when currentIndexValue becomes NaN
  currentIndexValueChanged(value, previousValue) {
    if (!Number.isFinite(value)) {
      this.currentIndexValue = Number.isFinite(previousValue) ? previousValue : 0
    }
  }

  // ========================================
  // POINTER SWIPE IMPLEMENTATION
  // ========================================

  bindPointerEvents() {
    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handlePointerMove = this.handlePointerMove.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)
    this.handlePointerCancel = this.handlePointerCancel.bind(this)

    // All listeners are passive - vertical scroll handled by browser, horizontal by JS
    this.element.addEventListener("pointerdown", this.handlePointerDown, { passive: true })
    this.element.addEventListener("pointermove", this.handlePointerMove, { passive: true })
    this.element.addEventListener("pointerup", this.handlePointerUp, { passive: true })
    this.element.addEventListener("pointercancel", this.handlePointerCancel, { passive: true })
  }

  unbindPointerEvents() {
    this.element.removeEventListener("pointerdown", this.handlePointerDown)
    this.element.removeEventListener("pointermove", this.handlePointerMove)
    this.element.removeEventListener("pointerup", this.handlePointerUp)
    this.element.removeEventListener("pointercancel", this.handlePointerCancel)
  }

  // Interactive element selector for pointer capture exclusion
  // Includes: HTML form elements, WAI-ARIA roles, focusable elements, Stimulus actions
  static INTERACTIVE_SELECTOR = [
    'button', 'a', 'input', 'select', 'textarea',   // HTML standard form elements
    '[role="button"]', '[role="link"]',              // WAI-ARIA custom buttons/links
    '[tabindex]',                                    // Programmatically focusable elements
    '[data-action]'                                  // Stimulus action bindings
  ].join(', ')

  handlePointerDown(event) {
    // Don't capture pointer events on interactive elements (buttons, links)
    // iOS WebKit doesn't fire click events properly when pointer is captured
    // This allows buttons inside the carousel to work normally
    if (event.target.closest(this.constructor.INTERACTIVE_SELECTOR)) {
      return
    }

    // Track single active pointer
    if (this.activePointerId !== null) return

    // If animation is in progress, complete it immediately
    // This prevents "tangled images" during rapid back-and-forth swipes
    if (this.pendingAnimationTimeout) {
      clearTimeout(this.pendingAnimationTimeout)
      this.pendingAnimationTimeout = null
      this.clearAllTransforms()
      this.updateDisplay()
    }

    this.activePointerId = event.pointerId
    this.touchStartXValue = event.clientX
    this.touchStartYValue = event.clientY
    this.touchCurrentXValue = event.clientX
    this.pointerStartTime = performance.now()
    this.pointerLockedAxis = null
    this.isSwipingValue = true
    this.lastPointerX = event.clientX

    // Keep receiving events even if pointer leaves element
    if (this.element.setPointerCapture) {
      this.element.setPointerCapture(event.pointerId)
    }

    // Note: iOS bounce control moved to handlePointerMove (horizontal axis confirmation)
    // This ensures page scroll rubber-band works normally for non-carousel gestures
  }

  handlePointerMove(event) {
    if (!this.isSwipingValue || event.pointerId !== this.activePointerId) return

    this.touchCurrentXValue = event.clientX

    const deltaX = this.touchCurrentXValue - this.touchStartXValue
    const absDeltaX = Math.abs(deltaX)
    const absDeltaY = Math.abs(event.clientY - this.touchStartYValue)

    // Native app (iOS/Android): Simple axis detection matching Android's approach
    // Uses same logic for both platforms - simple ratio-based vertical scroll detection
    if (this.isIOSNative() || this.isAndroidNative()) {
      if (!this.pointerLockedAxis) {
        // 15px threshold matches web behavior for consistent UX
        const minMoveThreshold = this.getUITimingConstant("CAROUSEL_AXIS_LOCK_THRESHOLD_PX", 15)
        if (absDeltaX > minMoveThreshold || absDeltaY > minMoveThreshold) {
          // Allow vertical scroll if vertical movement is significantly greater than horizontal
          // Default ratio 2.0: vertical must be 2x horizontal (63° angle) for scroll
          const verticalScrollRatio = this.getUITimingConstant("CAROUSEL_VERTICAL_SCROLL_RATIO", 2.0)

          if (absDeltaY > absDeltaX * verticalScrollRatio) {
            // Clear vertical intent → delegate to browser for native scroll
            this.pointerLockedAxis = "y"
            this.releasePointerAndReset(event)
            return
          }

          // All other gestures (horizontal, diagonal) → horizontal swipe
          this.pointerLockedAxis = "x"
          // iOS: Disable WKWebView bounce when starting horizontal swipe
          if (this.isIOSNative()) {
            this.notifyCarouselTouchStart()
          }
        }
      }
    } else {
      // Web browser: Use axis lock with threshold
      // CSS touch-action: pan-y allows native vertical scroll
      // Y-axis lock releases pointer capture for browser scroll handling
      const axisLockThreshold = this.getUITimingConstant("CAROUSEL_AXIS_LOCK_THRESHOLD_PX", 15)
      if (!this.pointerLockedAxis && (absDeltaX > axisLockThreshold || absDeltaY > axisLockThreshold)) {
        // Apply horizontal bias (1.2) to favor swipe gestures over scroll
        // This prevents diagonal gestures from incorrectly locking to Y axis
        // User intending horizontal swipe but starting slightly diagonal (e.g., 14px, 16px)
        // would fail without this bias
        const horizontalBias = this.getUITimingConstant("CAROUSEL_HORIZONTAL_BIAS", 1.2)
        const biasedDeltaX = absDeltaX * horizontalBias
        this.pointerLockedAxis = biasedDeltaX > absDeltaY ? "x" : "y"

        // Y-axis lock: release pointer capture to let browser handle vertical scroll
        if (this.pointerLockedAxis === "y") {
          this.releasePointerAndReset(event)
          return
        }
      }
    }

    // Apply visual drag feedback only for horizontal swipes
    if (this.pointerLockedAxis === "x") {
      this.applyDragFeedback(deltaX)
    }

    this.lastPointerX = event.clientX
  }

  // Apply transform feedback during drag to make carousel feel responsive
  applyDragFeedback(deltaX) {
    const currentSlide = this.slideTargets[this.currentIndexValue]
    if (!currentSlide) return

    const containerWidth = this.getContainerWidth()
    const maxIndex = this.slideTargets.length - 1

    // Determine direction and get adjacent slide
    const isSwipingLeft = deltaX < 0
    const adjacentIndex = isSwipingLeft
      ? this.currentIndexValue + 1
      : this.currentIndexValue - 1
    const adjacentSlide = this.slideTargets[adjacentIndex]

    // Apply resistance at edges (rubber band effect)
    let effectiveDelta = deltaX
    const isAtStart = this.currentIndexValue === 0 && deltaX > 0
    const isAtEnd = this.currentIndexValue === maxIndex && deltaX < 0
    if (isAtStart || isAtEnd) {
      // Apply resistance: diminishing returns as you drag further
      const resistanceCoefficient = this.getUITimingConstant("CAROUSEL_RESISTANCE_COEFFICIENT", 0.3)
      effectiveDelta = deltaX * resistanceCoefficient
    }

    // Transform current slide to follow finger
    currentSlide.style.transform = `translateX(${effectiveDelta}px)`
    currentSlide.style.transition = "none"

    // Show adjacent slide peeking in
    if (adjacentSlide) {
      // Make adjacent slide visible during drag
      adjacentSlide.style.visibility = "visible"
      adjacentSlide.style.opacity = "1"

      // Position adjacent slide: comes in from left (-100%) or right (+100%)
      const adjacentOffset = isSwipingLeft
        ? containerWidth + effectiveDelta  // Right slide coming in from right
        : -containerWidth + effectiveDelta  // Left slide coming in from left
      adjacentSlide.style.transform = `translateX(${adjacentOffset}px)`
      adjacentSlide.style.transition = "none"
    }
  }

  handlePointerUp(event) {
    if (!this.isSwipingValue || event.pointerId !== this.activePointerId) return

    const endX = this.lastPointerX ?? event.clientX
    const deltaX = endX - this.touchStartXValue
    const distance = Math.abs(deltaX)
    const absDeltaY = Math.abs(event.clientY - this.touchStartYValue)

    // Platform-specific swipe distance and fallback logic
    let minSwipeDistance, shouldProcessAsSwipe

    if (this.isIOSNative() || this.isAndroidNative()) {
      // Native (iOS/Android 공통): Use dedicated min swipe distance
      minSwipeDistance = this.getUITimingConstant("CAROUSEL_MIN_SWIPE_NATIVE_PX", 20)
      // Native fallback: If axis wasn't determined but movement is clearly horizontal, process as swipe
      // This handles cases where pointerUp fires before axis detection threshold is met
      const fallbackRatio = this.getUITimingConstant("CAROUSEL_SWIPE_FALLBACK_RATIO", 1.5)
      shouldProcessAsSwipe = this.pointerLockedAxis === "x" ||
        (this.pointerLockedAxis === null && distance > absDeltaY * fallbackRatio)
    } else {
      // Web: Use axis lock threshold as before
      minSwipeDistance = this.getUITimingConstant("CAROUSEL_AXIS_LOCK_THRESHOLD_PX", 15)
      // Web: Only process if explicitly locked to X axis (no fallback needed)
      shouldProcessAsSwipe = this.pointerLockedAxis === "x"
    }

    // Velocity-based flick detection (native apps): allow short but fast swipes
    const elapsedMs = performance.now() - this.pointerStartTime
    const velocityX = elapsedMs > 0 ? distance / elapsedMs : 0
    const minVelocity = this.getUITimingConstant("CAROUSEL_MIN_VELOCITY_NATIVE", 0.15)
    const isFlickGesture = (this.isIOSNative() || this.isAndroidNative()) && velocityX >= minVelocity

    // Check if we had horizontal drag (transforms applied)
    const hadHorizontalDrag = shouldProcessAsSwipe && (distance > minSwipeDistance || isFlickGesture)

    // Horizontal drag triggers slide transition
    if (hadHorizontalDrag) {
      const direction = deltaX > 0 ? -1 : 1  // -1 = previous, 1 = next
      this.animateSlideTransition(direction, 1)
      // iOS: Don't call touchEnd here - animation callback will handle it
      // Release pointer capture but keep bounce disabled until animation completes
      this.releasePointerCaptureOnly(event)
    } else if (shouldProcessAsSwipe) {
      // Swipe too short - snap back to current slide
      this.animateSnapBack()
      // iOS: Same - animation callback will handle touchEnd
      this.releasePointerCaptureOnly(event)
    } else {
      // No animation triggered - immediate cleanup
      this.releasePointerAndReset(event)
    }
  }

  // Animate slide transition with momentum effect
  animateSlideTransition(direction, steps) {
    // Cancel pending animation callback to prevent race condition on rapid swipes
    if (this.pendingAnimationTimeout) {
      clearTimeout(this.pendingAnimationTimeout)
      this.pendingAnimationTimeout = null
    }

    const currentSlide = this.slideTargets[this.currentIndexValue]
    const containerWidth = this.getContainerWidth()
    const maxIndex = this.slideTargets.length - 1

    // Calculate target index
    let targetIndex = this.currentIndexValue + (direction * steps)
    targetIndex = Math.max(0, Math.min(targetIndex, maxIndex))

    // If at boundary, bounce back
    if (targetIndex === this.currentIndexValue) {
      this.animateSnapBack()
      // iOS: WKWebView에서 CSS transition + Web Animations API 충돌
      // 드래그 저항 효과(0.3 coefficient)로 바운스 피드백 제공
      // Android/Web: bounce() 정상 동작
      if (!this.isIOSNative()) {
        this.bounce(direction > 0 ? "end" : "start")
      }
      return
    }

    const targetSlide = this.slideTargets[targetIndex]
    if (!currentSlide || !targetSlide) {
      this.clearAllTransforms()
      return
    }

    // Clear inline styles on slides NOT involved in this animation
    // This prevents "tangled images" when rapid swiping (e.g., 0→1→2)
    // Previous animation's slides would otherwise remain visible
    this.slideTargets.forEach((slide, index) => {
      if (index !== this.currentIndexValue && index !== targetIndex) {
        slide.style.transform = ""
        slide.style.transition = ""
        slide.style.visibility = ""
        slide.style.opacity = ""
      }
    })

    // Update index IMMEDIATELY (not in setTimeout)
    // This ensures rapid swipes use correct starting position
    this.currentIndexValue = targetIndex

    // Animation duration based on velocity (faster swipe = faster animation)
    const animationDuration = this.getUITimingConstant("CAROUSEL_SLIDE_ANIMATION_MS", 250)

    // Animate current slide out
    currentSlide.style.transition = `transform ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    currentSlide.style.transform = `translateX(${-direction * containerWidth}px)`

    // Animate target slide in
    targetSlide.style.visibility = "visible"
    targetSlide.style.opacity = "1"
    targetSlide.style.transition = `transform ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    targetSlide.style.transform = "translateX(0)"

    // After animation completes, clean up inline styles and re-enable bounce
    this.pendingAnimationTimeout = setTimeout(() => {
      this.clearAllTransforms()
      this.updateDisplay()
      this.pendingAnimationTimeout = null
      // iOS: Re-enable WKWebView bounce after animation completes
      // This prevents double-bounce during slide transition
      if (this.isIOSNative()) {
        this.notifyCarouselTouchEnd()
      }
    }, animationDuration)
  }

  // Animate snap back to current slide (cancelled swipe)
  animateSnapBack() {
    // Cancel pending animation callback to prevent race condition
    if (this.pendingAnimationTimeout) {
      clearTimeout(this.pendingAnimationTimeout)
      this.pendingAnimationTimeout = null
    }

    const currentSlide = this.slideTargets[this.currentIndexValue]
    if (!currentSlide) {
      this.clearAllTransforms()
      return
    }

    const animationDuration = this.getUITimingConstant("CAROUSEL_SNAP_BACK_MS", 200)

    // Animate current slide back to center
    currentSlide.style.transition = `transform ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    currentSlide.style.transform = "translateX(0)"

    // Animate adjacent slides back to off-screen
    const containerWidth = this.getContainerWidth()
    this.slideTargets.forEach((slide, index) => {
      if (index !== this.currentIndexValue) {
        slide.style.transition = `transform ${animationDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
        const offset = index < this.currentIndexValue ? -containerWidth : containerWidth
        slide.style.transform = `translateX(${offset}px)`
      }
    })

    // Clean up after animation and re-enable bounce
    this.pendingAnimationTimeout = setTimeout(() => {
      this.clearAllTransforms()
      this.pendingAnimationTimeout = null
      // iOS: Re-enable WKWebView bounce after snap-back animation completes
      if (this.isIOSNative()) {
        this.notifyCarouselTouchEnd()
      }
    }, animationDuration)
  }

  // Clear all inline styles, let CSS classes control visibility
  clearAllTransforms() {
    this.slideTargets.forEach(slide => {
      slide.style.transform = ""
      slide.style.transition = ""
      slide.style.visibility = ""
      slide.style.opacity = ""
    })
  }

  handlePointerCancel(event) {
    if (event.pointerId !== this.activePointerId) return
    this.clearAllTransforms()
    this.releasePointerAndReset(event)
  }

  resetPointerState() {
    // iOS: Re-enable native WKWebView bounce after carousel interaction ends
    // This is the single exit point for all gesture completion paths
    if (this.isIOSNative()) {
      this.notifyCarouselTouchEnd()
    }

    this.activePointerId = null
    this.pointerLockedAxis = null
    this.pointerStartTime = 0
    this.lastPointerX = null
    this.isSwipingValue = false
    this.touchStartXValue = 0
    this.touchStartYValue = 0
    this.touchCurrentXValue = 0
  }

  // Release pointer capture and reset gesture state
  // Used for: vertical scroll handoff, swipe completion, pointer cancellation
  releasePointerAndReset(event) {
    if (this.element.hasPointerCapture && this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId)
    }
    this.resetPointerState()
  }

  // Release pointer capture only, without resetting full state or calling touchEnd
  // Used when animation is started - touchEnd will be called in animation callback
  // This allows new touches to be handled while keeping bounce disabled during animation
  releasePointerCaptureOnly(event) {
    if (this.element.hasPointerCapture && this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId)
    }
    // Clear pointer ID to allow new gestures, but DON'T call notifyCarouselTouchEnd()
    // Animation callback will handle touchEnd when animation completes
    this.activePointerId = null
    this.isSwipingValue = false
    this.pointerLockedAxis = null
  }

  // ========================================
  // AUTO-SLIDE (chat tip slider only)
  // ========================================

  startAutoSlide() {
    this.stopAutoSlide() // Clear any existing interval
    const intervalMs = this.getUITimingConstant("CAROUSEL_AUTO_SLIDE_MS", 3000)
    this.autoSlideInterval = setInterval(() => {
      // Move to next slide, loop back to first if at end
      if (this.currentIndexValue < this.slideTargets.length - 1) {
        this.currentIndexValue++
      } else {
        this.currentIndexValue = 0
      }
      this.updateDisplay()
    }, intervalMs)
  }

  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval)
      this.autoSlideInterval = null
    }
  }

  // ========================================
  // NAVIGATION METHODS
  // ========================================

  // Stimulus passes event as first argument, so we ignore it and use fixed step
  next() {
    this.moveBy(1)
  }

  previous() {
    this.moveBy(-1)
  }

  moveBy(delta) {
    // NaN defense: Reset to 0 if currentIndexValue is invalid
    if (!Number.isFinite(this.currentIndexValue)) {
      this.currentIndexValue = 0
    }

    const target = this.currentIndexValue + delta
    const maxIndex = this.slideTargets.length - 1
    if (target < 0) {
      this.currentIndexValue = 0
      this.updateDisplay()
      this.bounce("start")
      return
    }
    if (target > maxIndex) {
      this.currentIndexValue = maxIndex
      this.updateDisplay()
      this.bounce("end")
      return
    }

    this.currentIndexValue = target
    this.updateDisplay()
  }

  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)

    // NaN defense: Ignore invalid index values
    if (!Number.isFinite(index) || index < 0 || index >= this.slideTargets.length) {
      return
    }

    this.currentIndexValue = index
    this.updateDisplay()
  }

  // ========================================
  // CAROUSEL STATE MANAGEMENT
  // ========================================

  updateDisplay() {
    // Early return if no slides available
    if (this.slideTargets.length === 0) return

    // NaN/bounds defense: Reset to 0 if currentIndexValue is invalid
    if (!Number.isFinite(this.currentIndexValue) ||
        this.currentIndexValue < 0 ||
        this.currentIndexValue >= this.slideTargets.length) {
      this.currentIndexValue = 0
    }

    // Update slides visibility via CSS classes only (inline styles cleared by clearAllTransforms)
    this.slideTargets.forEach((slide, index) => {
      if (index === this.currentIndexValue) {
        slide.classList.remove("carousel-slide-hidden")
        slide.classList.add("carousel-slide-visible")
      } else {
        slide.classList.add("carousel-slide-hidden")
        slide.classList.remove("carousel-slide-visible")
      }
    })

    // Lazy loading: current + adjacent slides only (prevents ~150-500KB upfront load)
    this.preloadAdjacentSlides()

    // Update indicators (supports both white and dark themes)
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, index) => {
        const isDarkTheme = indicator.dataset.theme === "dark"

        if (index === this.currentIndexValue) {
          // Active state
          indicator.classList.remove("bg-white/40", "bg-white/30", "bg-charcoal-800/30", "w-1", "w-1.5")
          indicator.classList.add("w-5")

          if (isDarkTheme) {
            // Tip slider: dark solid
            indicator.classList.remove("bg-white", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
            indicator.classList.add("bg-charcoal-800/80")
          } else {
            // Product detail: white with glow
            indicator.classList.remove("bg-charcoal-800/80")
            indicator.classList.add("bg-white", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
          }
        } else {
          // Inactive state
          indicator.classList.remove("bg-white", "bg-charcoal-800/80", "w-4", "w-5", "w-6", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
          indicator.classList.add("w-1.5")

          if (isDarkTheme) {
            // Tip slider: semi-transparent dark
            indicator.classList.remove("bg-white/40")
            indicator.classList.add("bg-charcoal-800/30")
          } else {
            // Product detail: semi-transparent white
            indicator.classList.remove("bg-charcoal-800/30")
            indicator.classList.add("bg-white/40")
          }
        }
      })
    }

    // Update counter (with NaN defense for edge cases)
    if (this.hasCounterTarget) {
      const safeIndex = Number.isFinite(this.currentIndexValue) ? this.currentIndexValue : 0
      this.counterTarget.textContent = `${safeIndex + 1} / ${this.slideTargets.length}`
    }

    // Update navigation button visibility (hide on first/last slide)
    if (this.hasPreviousButtonTarget && this.hasNextButtonTarget) {
      const isFirstSlide = this.currentIndexValue === 0
      const isLastSlide = this.currentIndexValue === this.slideTargets.length - 1

      // Toggle display classes together to avoid CSS specificity conflict
      // (hidden vs inline-flex: later class in CSS wins, so must remove conflicting class)
      this.toggleButtonVisibility(this.previousButtonTarget, !isFirstSlide)
      this.toggleButtonVisibility(this.nextButtonTarget, !isLastSlide)

      // Update ARIA attributes for accessibility
      this.previousButtonTarget.setAttribute("aria-disabled", isFirstSlide.toString())
      this.nextButtonTarget.setAttribute("aria-disabled", isLastSlide.toString())
    }

    // Update native status bar icon color based on current slide brightness
    // Only runs on Android native app with NativeBridge
    this.updateStatusBarForCurrentSlide()
  }

  // ========================================
  // NATIVE STATUS BAR INTEGRATION
  // ========================================

  /**
   * Analyze current slide image brightness and update native status bar icon color
   *
   * Android status bar icons can be light (white) or dark (black).
   * We sample the top 60px of the image (status bar area) and calculate
   * average brightness to determine optimal icon color for visibility.
   */
  updateStatusBarForCurrentSlide() {
    // Only proceed on Android native app with NativeBridge
    if (!window.NativeBridge || typeof window.NativeBridge.postMessage !== "function") {
      return
    }

    const slide = this.slideTargets[this.currentIndexValue]
    if (!slide) return

    // Find the image in the current slide
    const imageContainer = slide.querySelector("[data-controller~='progressive-image']")
    const img = imageContainer
      ? imageContainer.querySelector("[data-progressive-image-target='full']")
      : slide.querySelector("img")

    if (!img || !img.complete || !img.naturalWidth) {
      // Image not loaded yet, use default dark icons (safe for light backgrounds)
      this.setNativeStatusBarAppearance(true)
      return
    }

    // Check cache first to avoid expensive Canvas operations
    const cacheKey = img.src
    if (this.brightnessCache.has(cacheKey)) {
      const cachedBrightness = this.brightnessCache.get(cacheKey)
      const useDarkIcons = cachedBrightness >= this.brightnessDarkThresholdValue
      this.setNativeStatusBarAppearance(useDarkIcons)
      return
    }

    try {
      // Sample the top portion of the image (status bar area)
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")
      const sampleHeight = this.statusBarSampleHeightValue

      canvas.width = img.naturalWidth
      canvas.height = sampleHeight

      ctx.drawImage(img, 0, 0, img.naturalWidth, sampleHeight, 0, 0, canvas.width, sampleHeight)
      const imageData = ctx.getImageData(0, 0, canvas.width, sampleHeight)
      const brightness = this.calculateBrightness(imageData.data)

      // Cache the result with LRU eviction to prevent unbounded growth
      if (this.brightnessCache.size >= this.constructor.BRIGHTNESS_CACHE_MAX_SIZE) {
        // Remove oldest entry (Map maintains insertion order)
        const firstKey = this.brightnessCache.keys().next().value
        this.brightnessCache.delete(firstKey)
      }
      this.brightnessCache.set(cacheKey, brightness)

      // Dark image (below threshold) → light/white icons
      // Light image (at or above threshold) → dark icons
      const useDarkIcons = brightness >= this.brightnessDarkThresholdValue
      this.setNativeStatusBarAppearance(useDarkIcons)
    } catch (e) {
      // CORS or tainted canvas error - fallback to dark icons for light backgrounds
      console.debug("[Carousel] Brightness analysis failed (expected for cross-origin images):", e.name)
      this.setNativeStatusBarAppearance(true)
    }
  }

  /**
   * Calculate perceived brightness from image data using ITU-R BT.709 formula
   *
   * @param {Uint8ClampedArray} data - RGBA pixel data from getImageData()
   * @returns {number} Average brightness 0-255
   */
  calculateBrightness(data) {
    let sum = 0
    const pixelCount = data.length / 4

    for (let i = 0; i < data.length; i += 4) {
      // ITU-R BT.709 luminance formula (human perception weighted)
      // Y = 0.2126 R + 0.7152 G + 0.0722 B
      sum += data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722
    }

    return sum / pixelCount
  }

  /**
   * Request native status bar icon color change via NativeBridge
   *
   * @param {boolean} useDarkIcons - true for dark icons (light backgrounds),
   *                                  false for light/white icons (dark backgrounds)
   */
  setNativeStatusBarAppearance(useDarkIcons) {
    try {
      window.NativeBridge.postMessage(JSON.stringify({
        id: `statusbar_${Date.now()}`,
        type: "request",
        component: "statusBar",
        action: useDarkIcons ? "setDarkIcons" : "setLightIcons",
        data: {}
      }))
    } catch (e) {
      // Ignore errors - non-critical feature
    }
  }

  bounce(edge = "end") {
    // Animate only current slide to prevent layout shift (bottom nav shake fix)
    const currentSlide = this.slideTargets[this.currentIndexValue]
    if (!currentSlide) return

    const direction = edge === "start" ? 1 : -1
    const bounceOffset = this.getUITimingConstant("CAROUSEL_BOUNCE_OFFSET_PX", 12)
    const bounceDuration = this.getUITimingConstant("CAROUSEL_BOUNCE_ANIMATION_MS", 180)

    // Subtle translate animation to mimic native pager overscroll
    currentSlide.animate(
      [
        { transform: "translateX(0px)" },
        { transform: `translateX(${bounceOffset * direction}px)` },
        { transform: "translateX(0px)" }
      ],
      { duration: bounceDuration, easing: "cubic-bezier(0.33, 1, 0.68, 1)" }
    )
  }

  // Preload current + adjacent slides for smooth navigation
  // Triggers progressive-image controller to load images on-demand
  // Note: Slides 0-1 use eager mode (see hero_carousel_component.html.erb)
  // Slides 2+ use lazy mode - manual trigger here prevents flickering
  preloadAdjacentSlides() {
    // Load current slide + previous + next (max 3 slides at a time)
    // Skip slides 0-1: already eager-loaded via progressive_image_tag
    const indicesToLoad = [
      this.currentIndexValue - 1,  // Previous slide
      this.currentIndexValue,      // Current slide
      this.currentIndexValue + 1   // Next slide
    ].filter(index => {
      // Skip already-eager slides (0-1: progressive_image_tag with eager: true)
      if (index <= 1) return false
      return index >= 0 && index < this.slideTargets.length
    })

    indicesToLoad.forEach(index => {
      const slide = this.slideTargets[index]
      if (!slide) return

      // Find progressive-image controller within this slide
      const imageContainer = slide.querySelector('[data-controller~="progressive-image"]')
      if (!imageContainer) return

      // Images now use native browser lazy loading (loading="lazy" attribute)
      // No manual trigger needed - browser handles lazy loading automatically
    })
  }

  async openFullScreen() {
    const slide = this.slideTargets[this.currentIndexValue]
    if (!slide) return

    const images = this.collectFullscreenUrls()

    if (images.length > 0) {
      const result = await this.openNativeGallery(images)
      if (result) return
    }

    // Web fallback: open current image in new tab
    const directUrl = this.getFullscreenUrlFromSlide(slide)
    if (directUrl) {
      window.open(directUrl, "_blank", "noopener,noreferrer")
    }
  }

  collectFullscreenUrls() {
    const urls = this.slideTargets
      .map(slide => this.getFullscreenUrlFromSlide(slide))
      .filter(url => !!url)

    // Debug logging: Show format only (JPEG vs WebP) without exposing full URLs
    // Helps diagnose iOS WebP decode errors (err=-50)
    if (this.isIOSNative() && urls.length > 0) {
      const formats = urls.map(url => {
        if (url.includes('jpeg') || url.includes('.jpg')) return 'JPEG'
        if (url.includes('webp')) return 'WebP'
        if (url.includes('avif')) return 'AVIF'
        return 'unknown'
      })
      console.log('[Carousel iOS] Image formats:', formats.join(', '))
    }

    return urls
  }

  getFullscreenUrlFromSlide(slide) {
    // SSOT: slide.dataset.fullscreenUrl이 권위있는 소스
    // 서버에서 모든 슬라이드에 설정됨 (eager/lazy 무관)
    if (slide.dataset.fullscreenUrl) {
      return this.normalizeUrl(slide.dataset.fullscreenUrl)
    }

    // Fallback 1: progressive-image 컨테이너 (레거시/광고 캐러셀 지원)
    const imageContainer = slide.querySelector('[data-controller~="progressive-image"]')
    if (imageContainer) {
      const fullImg = imageContainer.querySelector('[data-progressive-image-target="full"]')
      const url =
        imageContainer.dataset.fullscreenUrl ||
        fullImg?.dataset?.progressiveImageSrc ||
        fullImg?.src
      return this.normalizeUrl(url)
    }

    // Fallback 2: 직접 img 요소 (비-progressive 슬라이드)
    const url = slide.querySelector("img")?.src
    return this.normalizeUrl(url)
  }

  normalizeUrl(url) {
    // Ignore placeholders and empty strings
    // Uses BusinessConstants from window (set by Rails in application.html.erb)
    const placeholderGif = window.BusinessConstants?.PLACEHOLDER_GIF_DATA_URI || ""
    if (!url || url === placeholderGif) return null

    // Convert relative URLs to absolute (required for Android native image loading)
    // url_for(image) returns relative paths like "/rails/active_storage/blobs/redirect/..."
    // but Android Coil library needs absolute URLs to load images
    if (url.startsWith("/")) {
      return `${window.location.origin}${url}`
    }

    return url
  }

  async openNativeGallery(images) {
    // Use cross-platform NativeBridge (supports both Android and iOS)
    if (!NativeBridge.isNative()) {
      return false
    }

    // Debug logging for native app (iOS/Android) image gallery
    if (typeof log !== 'undefined') {
      log.debug('[Carousel] openNativeGallery called', {
        imageCount: images.length,
        currentIndex: this.currentIndexValue,
        urls: images.slice(0, 3).map(url => url.substring(0, 80) + '...')
      })
    }

    try {
      // NativeBridge.ImageViewer.openGallery handles platform-specific bridge calls:
      // - Android: window.NativeBridge.postMessage()
      // - iOS: window.webkit.messageHandlers.nativeBridge.postMessage()
      await NativeBridge.ImageViewer.openGallery({
        images,
        index: this.currentIndexValue,
        aspect: "square",
        theme: "dark"
      })
      return true
    } catch (error) {
      // Fallback to web open when bridge is not available or fails
      console.error('[Carousel] openNativeGallery error:', error)
      return false
    }
  }

  getUITimingConstant(key, fallback) {
    const timing = window.BusinessConstants?.UITiming
    const value = timing?.[key]

    if (typeof value === "number" && !Number.isNaN(value)) {
      return value
    }

    return fallback
  }

  // ========================================
  // NATIVE APP DETECTION
  // ========================================

  /**
   * Detect if running in Turbo Native iOS app (WKWebView)
   *
   * Checks User-Agent for "Turbo Native iOS" marker set by AppConstants.swift
   * Result is cached for performance (User-Agent doesn't change mid-session)
   *
   * @returns {boolean} true if running in native iOS app
   */
  isIOSNative() {
    if (this._isIOSNative === null) {
      this._isIOSNative = /Turbo Native iOS/i.test(navigator.userAgent)
    }
    return this._isIOSNative
  }

  /**
   * Detect if running in Turbo Native Android app (WebView)
   *
   * Checks User-Agent for "Turbo Native Android" marker set by Toas2tApplication.kt
   * Result is cached for performance (User-Agent doesn't change mid-session)
   *
   * @returns {boolean} true if running in native Android app
   */
  isAndroidNative() {
    if (this._isAndroidNative === null) {
      this._isAndroidNative = /Turbo Native Android/i.test(navigator.userAgent)
    }
    return this._isAndroidNative
  }

  // ========================================
  // iOS NATIVE CAROUSEL OVERLAY
  // ========================================

  /**
   * Mount native iOS carousel overlay for 60fps swipe experience.
   * The native carousel sits on top of WKWebView and handles touch events.
   */
  async mountNativeCarousel() {
    if (this._nativeCarouselMounted) return

    // Get image container position (not full section, to preserve UI overlays)
    const container = this.hasImageContainerTarget
      ? this.imageContainerTarget
      : this.element
    const rect = container.getBoundingClientRect()
    const frame = {
      x: rect.left,
      y: rect.top + window.scrollY,  // Account for scroll position
      width: rect.width,
      height: rect.height
    }

    // Collect image URLs
    const images = this.collectFullscreenUrls()
    if (images.length === 0) return

    try {
      await NativeBridge.NativeCarousel.mount({
        frame,
        images,
        currentIndex: this.currentIndexValue,
        // Native UI data (buttons, share, edit)
        isOwner: this.isOwnerValue,
        shareTitle: this.shareTitleValue,
        shareText: this.shareTextValue,
        shareUrl: this.shareUrlValue,
        editUrl: this.editUrlValue,
        // Profile image overlay (Ads carousel only)
        profileImageUrl: this.profileImageUrlValue
      })

      this._nativeCarouselMounted = true

      // Hide JS carousel slides (native overlay handles display)
      this.slideTargets.forEach(slide => {
        slide.style.visibility = "hidden"
      })

      // Hide web profile overlay (native carousel renders its own profile image)
      const webProfileOverlay = this.element.querySelector('[data-carousel-profile-overlay]')
      if (webProfileOverlay) {
        webProfileOverlay.style.visibility = "hidden"
      }

      // Listen for native index change events
      this._unsubscribeIndexChanged = NativeBridge.on("nativeCarousel", "indexChanged", (data) => {
        const index = data?.index
        // Validate index is a valid number within bounds
        if (typeof index === "number" && index >= 0 && index < this.slideTargets.length) {
          this.currentIndexValue = index
          this.updateIndicatorsOnly()
        }
      })

      // Listen for native image load completion
      // iOS native carousel notifies JS when first image loads via Kingfisher
      this._unsubscribeImageLoaded = NativeBridge.on("nativeCarousel", "imageLoaded", (data) => {
        if (data?.index === 0) {
          // First image loaded - ensure all images are visible
          this.ensureImagesVisible()
        }
      })

      // Listen for scroll to update native carousel position
      this._handleScroll = () => this.updateNativeCarouselFrame()
      window.addEventListener("scroll", this._handleScroll, { passive: true })

    } catch (error) {
      console.warn("[carousel] Failed to mount native carousel:", error)
      // Fallback to JS carousel
      this._nativeCarouselMounted = false

      // Restore slide visibility (defensive - ensures slides are visible)
      this.slideTargets.forEach(slide => {
        slide.style.visibility = ""
      })

      // Restore web profile overlay visibility
      const webProfileOverlay = this.element.querySelector('[data-carousel-profile-overlay]')
      if (webProfileOverlay) {
        webProfileOverlay.style.visibility = ""
      }

      // Trigger WebView image load for slide 0 (fallback for progressive loading)
      this.triggerWebViewImageLoad()

      this.bindPointerEvents()
    }
  }

  /**
   * Trigger image load for slide 0 (fallback when native carousel mount fails).
   * No-op: Images now load directly via src attribute with skeleton background.
   * Kept for API compatibility with mountNativeCarousel() fallback path.
   */
  triggerWebViewImageLoad() {
    // Images load automatically via native browser loading (src attribute).
    // No manual trigger needed after LQIP removal.
  }

  /**
   * Unmount native iOS carousel overlay.
   */
  async unmountNativeCarousel() {
    if (!this._nativeCarouselMounted) return

    try {
      await NativeBridge.NativeCarousel.unmount()
    } catch (error) {
      // Ignore unmount errors
    }

    this._nativeCarouselMounted = false

    // Clean up event listeners
    if (this._unsubscribeIndexChanged) {
      this._unsubscribeIndexChanged()
      this._unsubscribeIndexChanged = null
    }
    if (this._unsubscribeImageLoaded) {
      this._unsubscribeImageLoaded()
      this._unsubscribeImageLoaded = null
    }

    // Clean up scroll listener
    if (this._handleScroll) {
      window.removeEventListener("scroll", this._handleScroll)
      this._handleScroll = null
    }

    // Restore JS carousel slide visibility
    this.slideTargets.forEach(slide => {
      slide.style.visibility = ""
    })

    // Restore web profile overlay visibility
    const webProfileOverlay = this.element.querySelector('[data-carousel-profile-overlay]')
    if (webProfileOverlay) {
      webProfileOverlay.style.visibility = ""
    }
  }

  /**
   * Observe turbo-frame modals on the same page (e.g., report_modal).
   * Hides native carousel when a modal frame loads content, restores when emptied.
   * Fixes z-order: native overlays render above WebView, blocking web modals.
   */
  observeModalFrames() {
    const modalFrame = document.getElementById("report_modal")
    if (!modalFrame) return

    this._modalFrameObserver = new MutationObserver(() => {
      const hasContent = modalFrame.children.length > 0
      if (hasContent && this._nativeCarouselMounted) {
        this._carouselHiddenForModal = true
        this.unmountNativeCarousel()
      } else if (!hasContent && this._carouselHiddenForModal) {
        this._carouselHiddenForModal = false
        this.mountNativeCarousel()
      }
    })

    this._modalFrameObserver.observe(modalFrame, { childList: true })
  }

  /**
   * Update native carousel frame position (called on scroll).
   */
  updateNativeCarouselFrame() {
    if (!this._nativeCarouselMounted) return

    // Use image container (not full section) for consistent positioning
    const container = this.hasImageContainerTarget
      ? this.imageContainerTarget
      : this.element
    const rect = container.getBoundingClientRect()
    const frame = {
      x: rect.left,
      y: rect.top + window.scrollY,
      width: rect.width,
      height: rect.height
    }

    NativeBridge.NativeCarousel.updateFrame(frame).catch(() => {
      // Ignore frame update errors
    })
  }

  /**
   * Update indicators only (for native carousel index sync).
   * Separated from updateDisplay() to avoid redundant work.
   */
  updateIndicatorsOnly() {
    // Update indicators (supports both white and dark themes)
    // Must match updateDisplay() styling exactly for consistent UX
    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((indicator, index) => {
        const isDarkTheme = indicator.dataset.theme === "dark"

        if (index === this.currentIndexValue) {
          // Active state (matches updateDisplay)
          indicator.classList.remove("bg-white/40", "bg-white/30", "bg-charcoal-800/30", "w-1", "w-1.5")
          indicator.classList.add("w-5")

          if (isDarkTheme) {
            // Tip slider: dark solid
            indicator.classList.remove("bg-white", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
            indicator.classList.add("bg-charcoal-800/80")
          } else {
            // Product detail: white with glow
            indicator.classList.remove("bg-charcoal-800/80")
            indicator.classList.add("bg-white", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
          }
        } else {
          // Inactive state (matches updateDisplay)
          indicator.classList.remove("bg-white", "bg-charcoal-800/80", "w-4", "w-5", "w-6", "shadow-[0_0_6px_rgba(255,255,255,0.6)]")
          indicator.classList.add("w-1.5")

          if (isDarkTheme) {
            // Tip slider: semi-transparent dark
            indicator.classList.remove("bg-white/40")
            indicator.classList.add("bg-charcoal-800/30")
          } else {
            // Product detail: semi-transparent white
            indicator.classList.remove("bg-charcoal-800/30")
            indicator.classList.add("bg-white/40")
          }
        }
      })
    }

    // Update counter
    if (this.hasCounterTarget) {
      const safeIndex = Number.isFinite(this.currentIndexValue) ? this.currentIndexValue : 0
      this.counterTarget.textContent = `${safeIndex + 1} / ${this.slideTargets.length}`
    }
  }

  /**
   * Ensure all images are visible.
   * Called when iOS native carousel notifies first image load completion.
   * Images now load directly (no LQIP), this is a safety check.
   */
  ensureImagesVisible() {
    this.slideTargets.forEach(slide => {

      // Safety: ensure full images are visible
      this.ensureSlideImagesVisible(slide)
    })
  }

  /**
   * Ensure full images are visible within a slide.
   * Safety check - images load directly with skeleton background.
   */
  ensureSlideImagesVisible(slide) {
    const fullElements = slide.querySelectorAll('[data-progressive-image-target="full"]')
    fullElements.forEach(full => {
      full.classList.remove("hidden", "invisible", "opacity-0")
      full.classList.add("opacity-100")
    })
  }

  // ========================================
  // iOS CAROUSEL BOUNCE CONTROL (Legacy - Android/Web only)
  // ========================================

  /**
   * Notify native iOS to disable WKWebView bounce during carousel touch.
   * Prevents "double bounce" effect at carousel edges.
   * Safe to call in web browser (no-op if not in native app).
   * Note: Only used when native carousel overlay is not mounted (Android/Web).
   */
  notifyCarouselTouchStart() {
    // Skip if native carousel overlay is handling touch (iOS)
    if (this._nativeCarouselMounted) return
    if (!NativeBridge.isIOS() || this._carouselBounceDisabled) return
    this._carouselBounceDisabled = true
    NativeBridge.send("carousel", "touchStart", {}).catch(() => {
      // Ignore errors - non-critical
    })
  }

  /**
   * Notify native iOS to re-enable WKWebView bounce after carousel touch ends.
   * Called from resetPointerState() which is the single exit point for all gesture paths.
   * Safe to call in web browser (no-op if not in native app).
   */
  notifyCarouselTouchEnd() {
    // Skip if native carousel overlay is handling touch (iOS)
    if (this._nativeCarouselMounted) return
    if (!NativeBridge.isIOS() || !this._carouselBounceDisabled) return
    this._carouselBounceDisabled = false
    NativeBridge.send("carousel", "touchEnd", {}).catch(() => {
      // Ignore errors - non-critical
    })
  }

  // Toggle button visibility by swapping hidden/inline-flex classes
  // Required because Tailwind CSS specificity: .inline-flex wins over .hidden
  toggleButtonVisibility(button, visible) {
    if (visible) {
      button.classList.remove("hidden")
      button.classList.add("inline-flex")
    } else {
      button.classList.add("hidden")
      button.classList.remove("inline-flex")
    }
  }

  // Get container width with fallback for edge cases (hidden element, etc.)
  getContainerWidth() {
    const minWidth = this.getUITimingConstant("CAROUSEL_CONTAINER_MIN_WIDTH_PX", 300)
    return this.element.offsetWidth || minWidth
  }

}
